import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useUser, SignInButton, SignUpButton, UserButton } from '@clerk/clerk-react';
import { loadJobs } from '../api/staticData';

const SERVER_URL = process.env.REACT_APP_SERVER_URL || 'http://localhost:3001';

interface JobTags {
  language?: string[];
  specialization?: string[];
  industry?: string[];
  infrastructure?: string[];
  companyStage?: string[];
  experienceLevel?: string[];
}

interface Job {
  id: string;
  title: string;
  location: string;
  locationType: string;
  salaryMin?: number;
  salaryMax?: number;
  tags?: JobTags;
  postedAt: string;
  company: {
    id: string;
    name: string;
    slug: string;
    logoUrl?: string;
    logoPath?: string;
  };
}

const TAG_COLORS: Record<string, string> = {
  language: 'bg-blue-100 text-blue-800',
  specialization: 'bg-purple-100 text-purple-800',
  industry: 'bg-green-100 text-green-800',
  infrastructure: 'bg-orange-100 text-orange-800',
  companyStage: 'bg-pink-100 text-pink-800',
  experienceLevel: 'bg-yellow-100 text-yellow-800',
};

const getDisplayTags = (tags?: JobTags): Array<{ category: string; value: string }> => {
  if (!tags) return [];
  const displayTags: Array<{ category: string; value: string }> = [];
  const categories: (keyof JobTags)[] = ['language', 'specialization', 'industry', 'infrastructure', 'companyStage', 'experienceLevel'];
  for (const category of categories) {
    const values = tags[category];
    if (values && values.length > 0) {
      displayTags.push({ category, value: values[0] });
    }
  }
  return displayTags;
};

const LandingPage: React.FC = () => {
  const { isSignedIn } = useUser();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const result = await loadJobs();
        setJobs(result.data.slice(0, 6));
      } catch (error) {
        console.error('Failed to fetch jobs:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchJobs();
  }, []);

  return (
    <>
      {/* Navigation */}
      <nav className="relative z-10 border-b border-oak-600/50">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-2xl">🏰</span>
              <span className="font-display text-xl font-bold text-amber-400">Guildhall.dev</span>
            </div>
            <div className="flex items-center gap-4">
              {isSignedIn ? (
                <>
                  <Link to="/dashboard" className="text-cream-200 hover:text-amber-400 transition">Dashboard</Link>
                  <UserButton
                    appearance={{
                      elements: {
                        avatarBox: "w-10 h-10"
                      }
                    }}
                  />
                </>
              ) : (
                <>
                  <SignInButton mode="modal">
                    <button className="text-cream-200 hover:text-amber-400 transition">Sign In</button>
                  </SignInButton>
                  <SignUpButton mode="modal">
                    <button className="bg-amber-500 hover:bg-amber-600 text-oak-900 px-4 py-2 rounded-lg font-semibold transition">
                      Join the Guild
                    </button>
                  </SignUpButton>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative z-10 py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="medieval-heading text-5xl md:text-6xl font-bold mb-16 lantern-glow tracking-wide">
            <span className="text-amber-400"><span className="initial-letter">T</span>ech Jobs</span> from the Source
          </h1>
          <p className="text-xl md:text-2xl text-cream-200/80 mb-8 max-w-2xl mx-auto text-left">
            <span className="drop-cap-letter">W</span>e find jobs where they're actually posted—Twitter threads, LinkedIn updates,
            Reddit comments, HN "Who's Hiring?" No scraped job boards. Real opportunities
            from real people.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            {isSignedIn ? (
              <Link to="/dashboard" className="bg-amber-500 hover:bg-amber-600 text-oak-900 px-8 py-4 rounded-lg font-bold text-lg transition glow-amber">
                Go to Dashboard
              </Link>
            ) : (
              <SignUpButton mode="modal">
                <button className="bg-amber-500 hover:bg-amber-600 text-oak-900 px-8 py-4 rounded-lg font-bold text-lg transition glow-amber">
                  Join the Guild Now
                </button>
              </SignUpButton>
            )}
            <a href="#how-it-works" className="border border-amber-500/50 text-amber-400 hover:bg-amber-500/10 px-8 py-4 rounded-lg font-semibold text-lg transition">
              How It Works
            </a>
          </div>

          {/* Trust badges */}
          <div className="flex flex-col items-center gap-4">
            <div className="flex flex-wrap justify-center gap-6 text-sm text-cream-200/60">
              <span className="flex items-center gap-2">
                <svg className="w-5 h-5 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                </svg>
                USA Tech Jobs Only
              </span>
              <span className="flex items-center gap-2">
                <svg className="w-5 h-5 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                </svg>
                Direct to Source
              </span>
              <span className="flex items-center gap-2">
                <svg className="w-5 h-5 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                </svg>
                No Subscription Required
              </span>
            </div>
            <p className="text-xs text-cream-200/50">
              GitHub account required for sign-up
            </p>
          </div>
        </div>
      </section>

      {/* Featured Jobs Preview */}
      <section className="relative z-10 py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="medieval-heading text-3xl font-bold text-center mb-12 tracking-wide">
            <span className="text-amber-400">Fresh from the Feed</span>
          </h2>

          {isLoading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-cream-100"></div>
              <p className="text-cream-200/70 mt-4">Loading jobs...</p>
            </div>
          ) : jobs.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-cream-200/70">No jobs available yet. Check back soon!</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {jobs.map((job) => (
                <div
                  key={job.id}
                  className="bg-cream-100 border border-cream-200 rounded-lg p-4 hover:border-gray-300 hover:shadow-md shadow-sm transition-all"
                >
                  <div className="flex items-center gap-4 mb-3">
                    {(job.company.logoPath || job.company.logoUrl) ? (
                      <img
                        src={job.company.logoPath
                          ? `${SERVER_URL}${job.company.logoPath}`
                          : job.company.logoUrl}
                        alt={`${job.company.name} logo`}
                        className="w-12 h-12 rounded-md object-contain bg-cream-100 flex-shrink-0"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-md bg-gray-200 flex items-center justify-center flex-shrink-0">
                        <span className="text-gray-600 text-lg font-semibold">
                          {job.company.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <h3 className="font-semibold text-gray-900 truncate">{job.title}</h3>
                      <p className="text-sm text-gray-600">{job.company.name}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
                    <span>📍 {job.location}</span>
                    {job.locationType === 'REMOTE' && (
                      <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded-full text-xs">Remote</span>
                    )}
                  </div>

                  {job.tags && getDisplayTags(job.tags).length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {getDisplayTags(job.tags).slice(0, 3).map(({ category, value }) => (
                        <span
                          key={`${category}-${value}`}
                          className={`px-2 py-0.5 rounded-full text-xs font-medium ${TAG_COLORS[category]}`}
                        >
                          {value}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="pt-3 border-t border-gray-200 flex items-center justify-between">
                    <span className="text-xs text-gray-500">
                      {new Date(job.postedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                    {!isSignedIn && (
                      <span className="text-xs text-amber-600 font-medium">Sign up to view details</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="text-center">
            {isSignedIn ? (
              <Link to="/dashboard" className="inline-flex items-center gap-2 text-amber-400 hover:text-amber-300 font-semibold transition">
                View all jobs
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"/>
                </svg>
              </Link>
            ) : (
              <SignInButton mode="modal" forceRedirectUrl="/dashboard">
                <button className="inline-flex items-center gap-2 text-amber-400 hover:text-amber-300 font-semibold transition">
                  View all jobs
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"/>
                  </svg>
                </button>
              </SignInButton>
            )}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="relative z-10 py-16 px-6 bg-oak-800/30">
        <div className="max-w-4xl mx-auto">
          <h2 className="medieval-heading text-3xl font-bold text-center mb-12 tracking-wide">
            <span className="text-amber-400">How the Guild Works</span>
          </h2>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-oak-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">🔍</span>
              </div>
              <h3 className="font-semibold text-lg mb-2 font-display">We Hunt</h3>
              <p className="text-cream-200/70 text-sm drop-cap text-left">
                Our community scouts Twitter, LinkedIn, Reddit, and HN for job posts from founders and hiring managers.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-oak-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">✅</span>
              </div>
              <h3 className="font-semibold text-lg mb-2 font-display">We Verify</h3>
              <p className="text-cream-200/70 text-sm drop-cap text-left">
                Every submission is reviewed by moderators. We link directly to the original post so you can verify.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-oak-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">🎯</span>
              </div>
              <h3 className="font-semibold text-lg mb-2 font-display">You Apply</h3>
              <p className="text-cream-200/70 text-sm drop-cap text-left">
                Get direct links to original posts and application pages. Apply directly with companies, no middleman.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="relative z-10 py-16 px-6 bg-oak-800/30">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="medieval-heading text-3xl font-bold mb-4 tracking-wide">
            <span className="text-amber-400">Join the Guild</span>
          </h2>
          <p className="text-cream-200/70 mb-8 drop-cap text-left max-w-md mx-auto">
            Full access to all features. Free during beta.
          </p>

          <div className="max-w-sm mx-auto">
            <div className="card-wood rounded-xl p-6 border-amber-500/50 relative">
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber-500 text-oak-900 text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide">
                Beta
              </span>
              <div className="text-2xl font-bold text-amber-400 mb-1">
                Free Access
              </div>
              <p className="text-sm text-cream-200/50 mb-4">During beta period</p>
              <ul className="text-sm text-cream-200/70 space-y-2 mb-6 text-left">
                <li className="flex items-center gap-2">
                  <span className="text-amber-500">✓</span> Full job details & apply links
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-amber-500">✓</span> Original source links
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-amber-500">✓</span> Track applications
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-amber-500">✓</span> Developers-only community
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-amber-500">✓</span> No spam or recruiter emails
                </li>
              </ul>
              <p className="text-xs text-cream-200/50 mb-4 text-center">
                GitHub account required
              </p>
              {isSignedIn ? (
                <Link to="/dashboard" className="block w-full bg-amber-500 hover:bg-amber-600 text-oak-900 py-3 rounded-lg font-bold transition glow-amber text-center">
                  Go to Dashboard
                </Link>
              ) : (
                <SignUpButton mode="modal">
                  <button className="block w-full bg-amber-500 hover:bg-amber-600 text-oak-900 py-3 rounded-lg font-bold transition glow-amber text-center">
                    Join the Guild Now
                  </button>
                </SignUpButton>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative z-10 py-20 px-6">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="medieval-heading text-3xl font-bold mb-4 tracking-wide">
            Find Your Next Role
          </h2>
          <p className="text-cream-200/70 mb-8 drop-cap text-left max-w-lg mx-auto">
            Skip the noise. Get jobs from the source. Join thousands of engineers who've found their next opportunity through the Guild.
          </p>
          {isSignedIn ? (
            <Link to="/dashboard" className="inline-block bg-amber-500 hover:bg-amber-600 text-oak-900 px-8 py-4 rounded-lg font-bold text-lg transition glow-amber">
              Go to Dashboard
            </Link>
          ) : (
            <SignUpButton mode="modal">
              <button className="inline-block bg-amber-500 hover:bg-amber-600 text-oak-900 px-8 py-4 rounded-lg font-bold text-lg transition glow-amber">
                Join the Guild Now
              </button>
            </SignUpButton>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-oak-600/50 py-8 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <span className="text-xl">🏰</span>
              <span className="font-display font-bold text-amber-400">Guildhall.dev</span>
            </div>
            <div className="text-sm text-cream-200/60">
              contact: admin@guildhall.dev
            </div>
            <div className="text-sm text-cream-200/40">
              © 2025 Guildhall.dev
            </div>
          </div>
        </div>
      </footer>
    </>
  );
};

export default LandingPage;
